import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import {
  ArchiveNoteRequest,
  ArchiveNoteResponse,
  CreateNoteRequest,
  CreateNoteResponse,
  DeleteNoteRequest,
  DeleteNoteResponse,
  GetClinicalNotesRequest,
  GetClinicalNotesResponse,
  GetPinnedNotesAndTotalCountRequest,
  GetPinnedNotesAndTotalCountResponse,
  UnArchiveNoteRequest,
  UnArchiveNoteResponse,
  UpdateNoteRequest,
  UpdateNoteResponse
} from '@/types/clinicalNote';

import { activityFeedApiSlice } from './activityFeedApiSlice';

export const clinicalNotesApiSlice = createApi({
  reducerPath: 'clinicalNotesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.ClinicalNotes],
  endpoints: (builder) => ({
    getNotesByPatientId: builder.query<GetClinicalNotesResponse, GetClinicalNotesRequest>({
      query: ({ patientId, limit, page }) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.GET_NOTES_BY_PATIENT_ID(patientId),
        params: { limit, page },
        method: 'GET'
      }),
      // Only have one cache entry because the arg always maps to one string
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return endpointName + '/' + queryArgs.patientId;
      },
      merge: (currentCache, newItems, { arg }) => {
        // If page 1, replace everything (refresh case)
        if (arg.page === 1) {
          return newItems;
        }

        if (currentCache.notes.length) {
          // Create a set of existing IDs for efficient lookup
          const existingNoteIds = new Set(currentCache.notes.map((note) => note.id));

          // Filter out duplicates from new items
          const uniqueNewNotes = newItems.notes.filter((note) => !existingNoteIds.has(note.id));

          return {
            ...currentCache,
            ...Object.fromEntries(Object.entries(newItems).filter(([key]) => key !== 'notes')),
            // Combine notes arrays without duplicates
            notes: [...currentCache.notes, ...uniqueNewNotes]
          };
        }

        return newItems;
      },
      providesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: patientId }
      ]
    }),
    getPinnedNotes: builder.query<
      GetPinnedNotesAndTotalCountResponse,
      GetPinnedNotesAndTotalCountRequest
    >({
      query: ({ patientId }) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.GET_PINNED_NOTES(patientId),
        method: 'GET'
      }),
      providesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: `pinned_${patientId}` }
      ]
    }),
    createNote: builder.mutation<CreateNoteResponse, CreateNoteRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.CREATE,
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: patientId }
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            activityFeedApiSlice.util.invalidateTags([
              { type: API_SLICE.ActivityFeed, id: arg.patientId }
            ])
          );
        } catch (err) {
          console.log('Error creating note:', err);
        }
      }
    }),
    updateNote: builder.mutation<UpdateNoteResponse, UpdateNoteRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.UPDATE(body.noteId),
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, { patientId, isPinned }) => {
        const tags = [{ type: API_SLICE.ClinicalNotes, id: patientId }] as any;
        if (isPinned !== undefined) {
          tags.push({
            type: API_SLICE.ClinicalNotes,
            id: `pinned_${patientId}`
          });
        }
        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            activityFeedApiSlice.util.invalidateTags([
              { type: API_SLICE.ActivityFeed, id: arg.patientId }
            ])
          );
        } catch (err) {
          console.log('Error updating note:', err);
        }
      }
    }),
    deleteNote: builder.mutation<DeleteNoteResponse, DeleteNoteRequest>({
      query: ({ noteId }) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.DELETE(noteId),
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: patientId }
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            activityFeedApiSlice.util.invalidateTags([
              { type: API_SLICE.ActivityFeed, id: arg.patientId }
            ])
          );
        } catch (err) {
          console.log('Error deleting note:', err);
        }
      }
    }),
    archiveNote: builder.mutation<ArchiveNoteResponse, ArchiveNoteRequest>({
      query: ({ noteId }) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.ARCHIVE(noteId),
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: patientId }
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            activityFeedApiSlice.util.invalidateTags([
              { type: API_SLICE.ActivityFeed, id: arg.patientId }
            ])
          );
        } catch (err) {
          console.log('Error archieving note:', err);
        }
      }
    }),
    unArchiveNote: builder.mutation<UnArchiveNoteResponse, UnArchiveNoteRequest>({
      query: ({ noteId }) => ({
        url: API_ENDPOINTS.CLINICAL_NOTES.UNARCHIVE(noteId),
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.ClinicalNotes, id: patientId }
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            activityFeedApiSlice.util.invalidateTags([
              { type: API_SLICE.ActivityFeed, id: arg.patientId }
            ])
          );
        } catch (err) {
          console.log('Error archieving note:', err);
        }
      }
    })
    // Add other endpoints here.
  })
});

export const {
  useGetNotesByPatientIdQuery,
  useGetPinnedNotesQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useArchiveNoteMutation,
  useUnArchiveNoteMutation
} = clinicalNotesApiSlice;
